import React, { useRef, useState } from 'react';
import { InternalText } from '../../InternalText';
import { useFullTheme } from '~/design';
import { Calendar as CalendarObj, RenderProps } from 'dayzed';
import { useLang } from '~/internal/LanguageContext';
import {
  addDays,
  addMonths,
  differenceInCalendarMonths,
  differenceInDays,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  setDate,
  startOfDay,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { flushSync } from 'react-dom';

const getDayNames = (lang: 'en-US' | 'fr-CA' | undefined) => {
  const dayNames =
    lang === 'fr-CA'
      ? ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi']
      : ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  return dayNames.map((dayName, idx) => ({
    label: dayName,
    value: idx,
  }));
};

export interface CalendarMonthProps {
  calendar: CalendarObj;
  getDateProps: RenderProps['getDateProps'];
  maxDate: Date;
  minDate: Date;
  moveByMonths: (val: number) => void;
  value: Date | undefined;
}

export function CalendarMonth({
  calendar,
  getDateProps,
  maxDate,
  minDate,
  moveByMonths,
  value,
}: CalendarMonthProps) {
  const theme = useFullTheme();
  const lang = useLang();
  const commonStyles = {
    height: '36px',
    width: '36px',
  };
  const tbodyRef = useRef<HTMLTableSectionElement>(null);
  const [numberToJumpTo, setNumberToJumpTo] = useState(0);

  const focusOnDateBtn = (date: Date) => {
    const el = tbodyRef.current?.querySelector(
      `button[data-date="${format(date, 'yyyy-MM-dd')}"]`,
    ) as HTMLButtonElement | undefined;
    el?.focus();
  };

  const moveFromDate = (e: React.KeyboardEvent<HTMLButtonElement>, date: Date) => {
    const key = e.key;
    const amountMap: Record<string, number> = {
      ArrowUp: -7,
      ArrowDown: 7,
      ArrowLeft: -1,
      ArrowRight: 1,
    };

    let amount = amountMap[key] ?? 0;
    if (key === 'Home') {
      const dateToMoveTo = startOfWeek(date);
      amount = differenceInDays(startOfDay(dateToMoveTo), startOfDay(date));
    } else if (key === 'End') {
      const dateToMoveTo = endOfWeek(date);
      amount = differenceInDays(startOfDay(dateToMoveTo), startOfDay(date));
    } else if (key === 'PageUp' || key === 'PageDown') {
      const monthOp = key === 'PageUp' ? subMonths : addMonths;
      const monthToMoveTo = e.shiftKey ? monthOp(date, 12) : monthOp(date, 1);
      const dateValueToSet = numberToJumpTo === 0 ? date.getDate() : numberToJumpTo;
      if (numberToJumpTo === 0) {
        setNumberToJumpTo(dateValueToSet);
      }

      const endOfMonthDate = endOfMonth(monthToMoveTo);
      let dateToMoveTo = monthToMoveTo;
      if (dateValueToSet <= endOfMonthDate.getDate()) {
        dateToMoveTo = setDate(dateToMoveTo, dateValueToSet);
      }

      if (isAfter(dateToMoveTo, maxDate)) {
        dateToMoveTo = maxDate;
      } else if (isBefore(dateToMoveTo, minDate)) {
        dateToMoveTo = minDate;
      }

      amount = differenceInDays(startOfDay(dateToMoveTo), startOfDay(date));
    }

    if (key !== 'PageUp' && key !== 'PageDown') {
      setNumberToJumpTo(0);
    }

    const direction = Math.sign(amount);
    const dateToMoveTo = addDays(date, amount);
    if (amount === 0) {
      return;
    } else if (direction > 0 && isAfter(dateToMoveTo, maxDate)) {
      return;
    } else if (direction < 0 && isBefore(dateToMoveTo, minDate)) {
      return;
    }

    const monthDiff = differenceInCalendarMonths(dateToMoveTo, date);
    if (monthDiff !== 0) {
      flushSync(() => moveByMonths(monthDiff));
    }

    e.preventDefault();
    focusOnDateBtn(dateToMoveTo);
  };

  const isSelectedInView =
    !!value &&
    (isSameDay(calendar.firstDayOfMonth, value) || isAfter(value, calendar.firstDayOfMonth)) &&
    (isSameDay(calendar.lastDayOfMonth, value) || isBefore(value, calendar.lastDayOfMonth));

  const isTodayInView =
    (isSameDay(calendar.firstDayOfMonth, new Date()) ||
      isAfter(new Date(), calendar.firstDayOfMonth)) &&
    (isSameDay(calendar.lastDayOfMonth, new Date()) ||
      isBefore(new Date(), calendar.lastDayOfMonth));

  const earliestAvailableDate = isBefore(minDate, calendar.firstDayOfMonth)
    ? calendar.firstDayOfMonth
    : minDate;

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
    <table role="grid" css={{ borderCollapse: 'separate' }}>
      <thead>
        <tr>
          {getDayNames(lang).map(weekday => (
            <th
              abbr={weekday.label}
              css={[commonStyles, { textAlign: 'center', padding: '1px !important' }]} // legacy.min.css has !important
              key={weekday.value}
              scope="col"
            >
              <InternalText align="center" intent="secondary" variant="labelM">
                {weekday.label.slice(0, 2)}
              </InternalText>
            </th>
          ))}
        </tr>
      </thead>

      <tbody ref={tbodyRef}>
        {calendar.weeks.map((week, weekIndex) => (
          <tr key={weekIndex}>
            {week.map((dateObj, index) => {
              const key = index;
              if (!dateObj) {
                return <td key={key} css={{ padding: '1px !important' }}></td>; // legacy.min.css has !important
              }
              const { date, selected, selectable, today } = dateObj;
              // Only 1 date in the month will be tabbable. The priority is:
              //  1. Selected date (if in view)
              //  2. Today (if in view)
              //  3. First selectable day of month
              const tabIndex = !selectable
                ? -1
                : selected
                  ? 0
                  : today && isSelectedInView
                    ? -1
                    : today
                      ? 0
                      : !isSelectedInView &&
                          !isTodayInView &&
                          isSameDay(date, earliestAvailableDate)
                        ? 0
                        : -1;

              return (
                <td
                  aria-selected={selected ? 'true' : 'false'}
                  key={key}
                  css={{ padding: '1px !important' }} // legacy.min.css has !important
                >
                  <button
                    {...getDateProps({ dateObj })}
                    aria-current={today ? 'date' : undefined}
                    css={[
                      commonStyles,
                      {
                        backgroundColor: 'transparent',
                        border: 0,
                        borderRadius: theme.borderRadiuses.circle,
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: theme.colors.bgRegularHover,
                        },
                        '&:focus-visible': {
                          outline: `2px solid ${theme.colors.actionPrimary}`,
                          outlineOffset: '1px',
                        },
                      },
                      selected && {
                        backgroundColor: theme.colors.actionPrimary,
                        '&:hover': {
                          backgroundColor: theme.colors.actionPrimaryHover,
                        },
                        '&:active': {
                          backgroundColor: theme.colors.actionPrimaryPressed,
                        },
                      },
                      today && {
                        border: `1px solid ${theme.colors.textSecondaryStark}`,
                      },
                      !selectable && {
                        cursor: 'not-allowed',
                        '&:hover': {
                          backgroundColor: 'transparent',
                        },
                      },
                    ]}
                    data-date={format(date, 'yyyy-MM-dd')}
                    onKeyDown={e => {
                      if (
                        [
                          'ArrowUp',
                          'ArrowDown',
                          'ArrowRight',
                          'ArrowLeft',
                          'Home',
                          'End',
                          'PageUp',
                          'PageDown',
                        ].includes(e.key)
                      ) {
                        moveFromDate(e, date);
                      }
                    }}
                    tabIndex={tabIndex}
                    type="button"
                  >
                    <InternalText
                      align="center"
                      contrast={!selectable ? 'low' : selected ? 'inverted' : undefined}
                      variant="bodyM"
                    >
                      {date.getDate()}
                    </InternalText>
                  </button>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
